import { useEffect, useRef, useState } from 'react';

/**
 * Poll an endpoint on an interval. Pauses while the tab is hidden; an
 * error keeps the last good data on screen and retries next tick, because
 * a monitoring surface that blanks out during a blip trains people to
 * distrust it.
 */
export function usePoll<T>(
  fetcher: () => Promise<T>,
  intervalMs: number,
  deps: readonly unknown[],
): { data: T | null; error: Error | null } {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);

    const tick = async () => {
      if (document.hidden) return;
      try {
        const result = await fetcherRef.current();
        if (!cancelled) {
          setData(result);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(err as Error);
      }
    };
    void tick();
    const id = window.setInterval(tick, intervalMs);

    // Ticks are skipped while the tab is hidden, so a Shell opened in a
    // background tab (ctrl-click, or a restored session) would sit on its
    // loading state until the next interval: 15s for the feed but 120s for
    // top and geo. Fetch the moment it becomes visible instead, so the
    // first thing anyone sees is data rather than a blank panel.
    const onVisibilityChange = () => {
      if (!document.hidden) void tick();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, error };
}

/** Aggregate an hourly series into n coarser points for a sparkline. */
export function bucketise(
  values: { bucket_start: string; value: number }[],
  points: number,
  hours: number,
  now: () => number = Date.now,
): number[] {
  const out = new Array<number>(points).fill(0);
  const windowMs = hours * 3_600_000;
  const start = now() - windowMs;
  for (const v of values) {
    const t = new Date(v.bucket_start).getTime();
    if (t < start) continue;
    const idx = Math.min(points - 1, Math.floor(((t - start) / windowMs) * points));
    out[idx] = (out[idx] ?? 0) + v.value;
  }
  return out;
}
